import { Popover, PopoverContent, PopoverTrigger } from '@ifixit/ui/components/popover';
import { FaIcon } from '@ifixit/ui/components/icon';
import { useTimeout } from '@ifixit/ui/hooks/use-timeout';
import { _js, ___p } from '@ifixit/utils/localize';
import {
   useContext,
   useRef,
   useState,
   Fragment,
   MouseEventHandler,
   ReactNode,
   SVGProps,
} from 'react';
import { GuideFavoriteContext } from './GuideFavorite';
import { GuideCompletionContext } from './GuideComplete';
import { GuideData } from './GuideHeader';
import { localizeAndFormat } from 'Shared/utils/numberFormat';

export function GuideStats({
   isTeardown,
   guideCommentCount,
   stepCommentCount,
   viewCounts,
}: Pick<GuideData, 'isTeardown' | 'guideCommentCount' | 'stepCommentCount' | 'viewCounts'>) {
   const stats = [
      <ViewStat key="views" viewCounts={viewCounts} />,
      <PageCommentsStat
         key="comments"
         guideCommentCount={guideCommentCount}
         stepCommentCount={stepCommentCount}
      />,
      <CompletionStat key="completions" />,
      ...(isTeardown ? [] : [<FavoriteStat key="favorites" isTeardown={isTeardown} />]),
   ];

   return (
      <div className="flex items-center gap-3">
         {stats.map((stat, index) => (
            <Fragment key={index}>
               {index > 0 && <div className="w-px h-5 bg-gray-400" />}
               {stat}
            </Fragment>
         ))}
      </div>
   );
}

const statButtonClass =
   'flex items-center font-medium m-0 p-0 bg-transparent border-none cursor-pointer text-gray-500 visited:text-gray-500 hover:text-gray-700 hover:no-underline [&_svg]:mr-1';

/**
 * Hover-triggered popover that stays open while the pointer is over the
 * content, so the CTA button inside remains clickable. Replaces Chakra's
 * `trigger="hover"` popover (and the HoverPopoverTrigger a11y hack: as a
 * Popover trigger, it only gets popup aria attributes, which are valid here
 * because the content is interactive).
 *
 * The trigger must be a plain `type="button"` element: while the popover is
 * open, its click is default-prevented to suppress the popover's click
 * toggle, which would cancel navigation on a link or submission on a submit
 * button.
 */
export function HoverPopover({
   trigger,
   children,
}: {
   trigger: ReactNode;
   children: (_close: () => void) => ReactNode;
}) {
   const [open, setOpen] = useState(false);
   const isPointerOverTrigger = useRef(false);
   const triggerRef = useRef<HTMLButtonElement>(null);
   const suppressOpenOnFocus = useRef(false);
   const closeTimer = useTimeout(() => setOpen(false), 150);
   const openNow = () => {
      closeTimer.cancel();
      setOpen(true);
   };

   return (
      <Popover open={open} onOpenChange={setOpen}>
         <PopoverTrigger
            asChild
            ref={triggerRef}
            onMouseEnter={() => {
               isPointerOverTrigger.current = true;
               openNow();
            }}
            onMouseLeave={() => {
               isPointerOverTrigger.current = false;
               closeTimer.start();
            }}
            onFocus={() => {
               if (!suppressOpenOnFocus.current) {
                  openNow();
               }
            }}
            onBlur={closeTimer.start}
            onClick={event => {
               if (open) {
                  event.preventDefault();
               }
            }}
         >
            {trigger}
         </PopoverTrigger>
         <PopoverContent
            onMouseEnter={closeTimer.cancel}
            onMouseLeave={closeTimer.start}
            onFocus={closeTimer.cancel}
            onBlur={closeTimer.start}
            onOpenAutoFocus={event => {
               if (isPointerOverTrigger.current) {
                  event.preventDefault();
               }
            }}
            onCloseAutoFocus={event => {
               // Focus coming back from the content must not reopen it.
               event.preventDefault();
               suppressOpenOnFocus.current = true;
               // focus() dispatches focusin synchronously, so the trigger's
               // onFocus consumes the flag before the reset below.
               triggerRef.current?.focus({ preventScroll: true });
               suppressOpenOnFocus.current = false;
            }}
            className="bg-gray-800 text-white border-none rounded-md p-3 text-sm w-auto max-w-xs"
         >
            {children(() => setOpen(false))}
         </PopoverContent>
      </Popover>
   );
}

function ViewStat({ viewCounts }: Pick<GuideData, 'viewCounts'>) {
   return (
      <HoverPopover
         trigger={
            <button type="button" className={statButtonClass}>
               <FaIcon name="eye" style="regular" className="size-4 self-center text-gray-700" />
               {localizeAndFormat(viewCounts.all, { notation: 'compact' })}
            </button>
         }
      >
         {() => (
            <div className="flex flex-col w-full gap-2">
               <div className="flex items-center self-start gap-1">
                  <StatsIcon width="20" height="20" />
                  <p className="m-0 text-gray-300 text-sm font-normal">{_js('View statistics:')}</p>
               </div>
               <ViewStatSection
                  text={_js('Past 24 Hours:')}
                  stat={localizeAndFormat(viewCounts.today)}
               />
               <ViewStatSection
                  text={_js('Past 7 Days:')}
                  stat={localizeAndFormat(viewCounts.week)}
               />
               <ViewStatSection
                  text={_js('Past 30 Days:')}
                  stat={localizeAndFormat(viewCounts.month)}
               />
               <ViewStatSection text={_js('All Time:')} stat={localizeAndFormat(viewCounts.all)} />
            </div>
         )}
      </HoverPopover>
   );
}

export function PageCommentsStat({
   guideCommentCount,
   stepCommentCount,
}: Pick<GuideData, 'guideCommentCount' | 'stepCommentCount'>) {
   const totalCount = guideCommentCount + stepCommentCount;
   const text = getCommentText(guideCommentCount, stepCommentCount);
   // Show the button to the guide comments if there are guide comments, or there are no comments at all.
   // Do not show if there are step comments only.
   const showGuideCommentsButton = guideCommentCount > 0 || totalCount == 0;

   return (
      <HoverPopover
         trigger={
            <button type="button" className={statButtonClass}>
               <FaIcon name="messages" style="regular" className="size-4 text-gray-700" />
               {totalCount}
            </button>
         }
      >
         {close =>
            showGuideCommentsButton ? (
               <TextButtonContent
                  text={text}
                  icon="messages"
                  iconStyle="regular"
                  buttonText={_js('Go to guide comments')}
                  onButtonClick={() => {
                     const comments = document.querySelector<HTMLElement>('.js-page-comments');
                     if (comments) {
                        comments.tabIndex = -1;
                        comments.addEventListener(
                           'blur',
                           () => comments.removeAttribute('tabindex'),
                           { once: true }
                        );
                        comments.focus({ preventScroll: true });
                     }
                     close();
                     comments?.scrollIntoView({ behavior: 'smooth' });
                  }}
               />
            ) : (
               <p className="m-0 whitespace-pre-line">{text}</p>
            )
         }
      </HoverPopover>
   );
}

function getCommentText(guideCommentCount: number, stepCommentCount: number): string {
   const hasGuideComment = guideCommentCount > 0;
   const hasStepComment = stepCommentCount > 0;

   if (!hasGuideComment && !hasStepComment) {
      return _js('No one has left comments on this guide. Will you be first?');
   }

   const guideCommentText = hasGuideComment
      ? ___p(
           guideCommentCount,
           '%1 guide comment.',
           '%1 guide comments.',
           guideCommentCount.toString()
        )
      : '';
   const stepCommentText = ___p(
      stepCommentCount,
      '%1 comment on steps',
      '%1 comments on steps',
      stepCommentCount.toString()
   );

   if (hasGuideComment && !hasStepComment) {
      return guideCommentText;
   }

   if (hasStepComment && !hasGuideComment) {
      return stepCommentText + '.';
   }

   return stepCommentText + ', ' + guideCommentText;
}

function CompletionStat() {
   const guideCompletionContext = useContext(GuideCompletionContext);

   const onClick = () =>
      guideCompletionContext.isCompleted
         ? guideCompletionContext.removeComplete(App.guideid)
         : guideCompletionContext.complete(App.guideid);
   const iconStyle = guideCompletionContext.isCompleted ? 'solid' : 'regular';

   const noCompletionsText = _js(
      'No one has marked this guide "completed." Will you be the first?'
   );

   const completedText = ___p(
      guideCompletionContext.completedCount,
      '%1 person has marked this guide "completed." It\'s you — great job!',
      '%1 people have marked this guide "completed." Including you — great job!',
      guideCompletionContext.completedCount.toString()
   );

   const notCompletedText = ___p(
      guideCompletionContext.completedCount,
      '%1 person has marked this guide "completed." Will you be next?',
      '%1 people have marked this guide "completed." Will you be next?',
      guideCompletionContext.completedCount.toString()
   );

   const text =
      guideCompletionContext.completedCount == 0
         ? noCompletionsText
         : guideCompletionContext.isCompleted
           ? completedText
           : notCompletedText;

   const buttonText = guideCompletionContext.isCompleted
      ? _js('Remove completion')
      : _js('Mark complete');

   return (
      <HoverPopover
         trigger={
            <button type="button" className={statButtonClass}>
               <FaIcon name="circle-check" style={iconStyle} className="size-4 text-green-500" />
               {guideCompletionContext.completedCount}
            </button>
         }
      >
         {() => (
            <TextButtonContent
               text={text}
               buttonText={buttonText}
               icon="circle-check"
               iconStyle={iconStyle}
               onButtonClick={onClick}
            />
         )}
      </HoverPopover>
   );
}

function FavoriteStat({ isTeardown }: Pick<GuideData, 'isTeardown'>) {
   const guideFavoriteContext = useContext(GuideFavoriteContext);
   const onClick = () => {
      if (guideFavoriteContext.isFavorite) {
         guideFavoriteContext.unfavorite(App.guideid, App.langid, isTeardown);
      } else {
         guideFavoriteContext.favorite(App.guideid, App.langid, isTeardown);
      }
   };
   const iconStyle = guideFavoriteContext.isFavorite ? 'solid' : 'regular';

   const noFavoritesText = _js(
      'No one has favorited (and subscribed to) this guide. Will you be the first?'
   );

   const favoritedText = ___p(
      guideFavoriteContext.favoriteCount,
      "%1 person has favorited (and subscribed to) this guide — and that's you!",
      '%1 people have favorited (and subscribed to) this guide — including you!',
      guideFavoriteContext.favoriteCount.toString()
   );

   const notFavoritedText = ___p(
      guideFavoriteContext.favoriteCount,
      '%1 person has favorited (and subscribed to) this guide. Will you be next?',
      '%1 people have favorited (and subscribed to) this guide. Will you be next?',
      guideFavoriteContext.favoriteCount.toString()
   );

   const text =
      guideFavoriteContext.favoriteCount == 0
         ? noFavoritesText
         : guideFavoriteContext.isFavorite
           ? favoritedText
           : notFavoritedText;

   const buttonText = guideFavoriteContext.isFavorite
      ? _js('Remove favorite')
      : _js('Add favorite');
   return (
      <HoverPopover
         trigger={
            <button type="button" className={statButtonClass}>
               <FaIcon name="star" style={iconStyle} className="size-4 text-yellow-500" />
               {guideFavoriteContext.favoriteCount}
            </button>
         }
      >
         {() => (
            <TextButtonContent
               text={text}
               onButtonClick={onClick}
               buttonText={buttonText}
               icon="star"
               iconStyle={iconStyle}
            />
         )}
      </HoverPopover>
   );
}

function TextButtonContent({
   icon,
   iconStyle = 'solid',
   text,
   buttonText,
   onButtonClick,
}: {
   icon: string;
   iconStyle?: 'solid' | 'regular';
   text: string;
   buttonText: string;
   onButtonClick: MouseEventHandler<HTMLButtonElement>;
}) {
   return (
      <div className="grid">
         <p className="m-0 whitespace-pre-line">{text}</p>
         <div className="flex w-full justify-end">
            <button
               type="button"
               className="flex items-center shrink justify-end mt-2 px-3 py-1.5 rounded-md bg-brand-500 hover:bg-brand-600 text-white font-semibold border-none cursor-pointer"
               onClick={onButtonClick}
            >
               {icon && <FaIcon name={icon} style={iconStyle} className="size-4 mr-2 text-white" />}
               <span>{buttonText}</span>
            </button>
         </div>
      </div>
   );
}

function StatsIcon(props: SVGProps<SVGSVGElement>) {
   return (
      <svg
         width="20"
         height="20"
         viewBox="0 0 20 20"
         fill="none"
         xmlns="http://www.w3.org/2000/svg"
         {...props}
      >
         <path
            d="M16.625 2.78125C17.0312 2.4375 17.125 1.8125 16.7812 1.375C16.4375 0.96875 15.7812 0.875 15.375 1.21875L10.9688 4.75L7.59375 2.21875C7.21875 1.9375 6.71875 1.9375 6.375 2.21875L1.375 6.21875C0.9375 6.5625 0.875 7.21875 1.21875 7.625C1.5625 8.0625 2.1875 8.125 2.625 7.78125L7 4.28125L10.375 6.8125C10.75 7.09375 11.25 7.09375 11.625 6.78125L16.625 2.78125ZM6 8V14C6 14.5625 6.4375 15 7 15C7.53125 15 8 14.5625 8 14V8C8 7.46875 7.53125 7 7 7C6.4375 7 6 7.46875 6 8ZM2 11V14C2 14.5625 2.4375 15 3 15C3.53125 15 4 14.5625 4 14V11C4 10.4688 3.53125 10 3 10C2.4375 10 2 10.4688 2 11ZM11 9C10.4375 9 10 9.46875 10 10V14C10 14.5625 10.4375 15 11 15C11.5312 15 12 14.5625 12 14V10C12 9.46875 11.5312 9 11 9ZM14 8V14C14 14.5625 14.4375 15 15 15C15.5312 15 16 14.5625 16 14V8C16 7.46875 15.5312 7 15 7C14.4375 7 14 7.46875 14 8Z"
            fill="#6B7280"
         />
      </svg>
   );
}

function ViewStatSection({ text, stat }: { text: string; stat: string }) {
   return (
      <div className="flex w-full rounded bg-gray-700">
         <p className="m-0 grow ml-3 text-gray-300 text-xs font-normal">{text}</p>
         <p className="m-0 mr-3 text-gray-100 text-sm font-semibold">{stat}</p>
      </div>
   );
}
